import { load } from '@fingerprintjs/fingerprintjs';
import { useMutation } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { BatchTypeEnum, X_VISITOR_ID } from '@e-origin/shared';

import { AUTH_COOKIE_NAME, AUTH_EMAIL_NAME } from '@constants';
import { showErrorToast } from '@hooks/showErrorToast';
import { apiClient, ApiError, STORAGE_KEYS } from '@utils';
import { getStoredCriteria, setStoredCriteria } from '../../criteria.caching';
import { useTokenManager } from './useTokenManager';

interface SignInPayload {
  email: string;
  password: string;
  visitorId?: string;
  recaptchaToken?: string;
}

interface SignInResponse {
  token: string;
}
const setCookie = (name: string, value: string) => {
  if (!Cookies.get(name) || Cookies.get(name) === undefined) {
    Cookies.set(name, value);
  }
};

export const setUserStorage = (email: string, token: string) => {
  localStorage.setItem(AUTH_EMAIL_NAME, email);
  Cookies.set(AUTH_COOKIE_NAME, token);
  if (!getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW)) {
    setStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW, BatchTypeEnum.HIGH_VALUES);
  }
  setCookie('sending', 'true');
  setCookie('riskAnalysis', 'true');
  setCookie('showAll', 'true');
  setCookie('informations', 'true');
};

export const useSignIn = (callback: () => void) => {
  const { scheduleTokenRefresh } = useTokenManager();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useMutation<void, ApiError, SignInPayload>({
    mutationFn: async ({ email, password }) => {
      const fp = await load();
      const { visitorId } = await fp.get();
      let recaptchaToken = '';

      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('login');
      }

      const result = await apiClient.user.loginUser.mutation({
        body: {
          email,
          password,
          visitorId,
          recaptchaToken,
        },
      });

      if (result.status === 200 && result.body.token) {
        setUserStorage(email, result.body.token);
        setCookie(X_VISITOR_ID, visitorId);
        scheduleTokenRefresh();
      } else throw result.body;
    },
    onSuccess: () => {
      callback();
    },
    onError: (error) => {
      showErrorToast(error, `Authentication failed!`);
    },
  });
};
